import { Divider } from '@aon/aon-tws-common-ui';
import { createSelector } from '@reduxjs/toolkit';
import * as React from 'react';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import { RootState, useAppDispatch, useAppSelector } from '../../appStore';
import { CardLayout, Section } from '../../components/common';
import { SurveyNoteHeaderContainer } from '../../components/notes/SurveyNoteHeaderContainer';
import { BrandingAndTheme } from '../../components/pages/branding-and-theme';
import ContentConstants from '../../contentConstants';
import { SurveyStep } from '../../models';
import { ActionTypes } from '../../store/ActionTypes';
import { loadSurveyImage } from '../../store/SurveyImageSlice';
import { loadSurveyTheme } from '../../store/SurveyThemeSlice';
import { loadThemes } from '../../store/ThemeSlice';
import { PreviousStepButton, SaveAndNextButton, SaveButton, SaveButtonsGroup } from '../save-buttons/SaveButtonsGroup';
import { SurveyPageStateSetter } from '../state-setter/SurveyPageStateSetter';
import { PageStateSetterRouteProps } from '../state-setter/SurveyPageStateSetterBase';

const selectBrandingAndThemeStatus = createSelector(
	[
		(state: RootState) => state.survey.isLoading,
		(state: RootState) => state.surveyBuilder.saving,
		(state: RootState) => state.surveyImage.status,
		(state: RootState) => state.surveyTheme.status,
		(state: RootState) => state.themes.isLoading,
	],
	(isSurveyLoading, isSurveySaving, surveyImageStatus, surveyThemeStatus, isThemesLoading) => {
		if (isSurveyLoading || surveyImageStatus === 'loading' || surveyThemeStatus === 'loading' || isThemesLoading) {
			return 'loading';
		}

		if (isSurveySaving || surveyImageStatus === 'saving' || surveyThemeStatus === 'saving') {
			return 'saving';
		}

		return 'idle';
	},
);

const selectIsBrandingAndThemeValid = createSelector(
	[(state: RootState) => state.surveyImage.validationResult.isValid, (state: RootState) => state.surveyTheme.validationResult.isValid],
	(isSurveyImageValid, isSurveyThemeValid) => isSurveyImageValid && isSurveyThemeValid,
);

export const BrandingAndThemePage = (props: PageStateSetterRouteProps) => {
	const dispatch = useAppDispatch();
	const params = useParams<{ surveyId: string }>();
	const surveyId = +params.surveyId;

	useEffect(() => {
		let ignore = false;

		const load = async () => {
			const results = await Promise.allSettled([
				dispatch(loadSurveyImage(surveyId)).unwrap(),
				dispatch(loadThemes()).unwrap(),
				dispatch(loadSurveyTheme(surveyId)).unwrap(),
			]);

			if (!ignore && results.some((result) => result.status === 'rejected')) {
				dispatch({ type: ActionTypes.TOAST_WARNING_NOTIFICATION, payload: ContentConstants.TOAST_ERROR_GENERIC_LOAD_BODY });
			}
		};

		void load();

		return () => {
			ignore = true;
		};
	}, [dispatch, surveyId]);

	const status = useAppSelector(selectBrandingAndThemeStatus);
	const isValid = useAppSelector(selectIsBrandingAndThemeValid);

	const saveBtnDisabled = status === 'loading' || status === 'saving' || !isValid;

	return (
		<>
			<CardLayout loading={status === 'loading'} title={ContentConstants.TITLE_DESIGN_YOUR_SURVEY} header={<SurveyNoteHeaderContainer />}>
				<Section sectionHeader={ContentConstants.SECTIONHEADERS_SURVEY_BRANDING_AND_THEME}>
					<BrandingAndTheme />
				</Section>
				<Divider type='dashed' />
				<SaveButtonsGroup>
					<PreviousStepButton />
					<SaveButton disabled={saveBtnDisabled} />
					<SaveAndNextButton disabled={saveBtnDisabled} />
				</SaveButtonsGroup>
			</CardLayout>
			<SurveyPageStateSetter {...props} currentStep={SurveyStep.BrandingAndTheme} />
		</>
	);
};
